import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { errorSnackBar, successSnackBar } from "../actions/common.action";

const OAUTH_RESULT_TYPES = {
  XERO_CONNECTION_RESULT: 'Xero',
  QUICKBOOKS_CONNECTION_RESULT: 'QuickBooks',
  MYOB_CONNECTION_RESULT: 'MYOB'
};

const STORAGE_KEYS = {
  xero_connection_result: 'Xero',
  quickbooks_connection_result: 'QuickBooks',
  myob_connection_result: 'MYOB'
};

function useEventListener(eventName, handler) {
  useEffect(() => {
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [eventName, handler]);
}

export function useAccountingOAuth(accountId, fetchConnections) {
  const dispatch = useDispatch();

  const processConnectionResult = useCallback(async (result = {}, platformName) => {
    const { status, error } = result;

    if (status === 'success') {
      dispatch(successSnackBar(`${platformName} Connected Successfully`));
      await fetchConnections();
    } else if (error && error !== 'access_denied') {
      dispatch(errorSnackBar(error || `Failed to connect to ${platformName}`));
    }
  }, [fetchConnections, dispatch]);

  const handleOAuthMessage = useCallback(async (e) => {
    const platformName = OAUTH_RESULT_TYPES[e.data?.type];
    if (platformName) {
      await processConnectionResult(e.data, platformName);
    }
  }, [processConnectionResult]);

  const handleStorageChange = useCallback(async (e) => {
    const platformName = STORAGE_KEYS[e?.key];
    if (!platformName || !e?.newValue) return;

    try {
      const result = JSON.parse(e.newValue);
      await processConnectionResult(result, platformName);
      localStorage.removeItem(e.key);
    } catch (err) { // Fix: catch keyword added here
      console.error(`Error handling ${platformName} callback storage event:`, err);
    }
  }, [processConnectionResult]);

  useEventListener('message', handleOAuthMessage);
  useEventListener('storage', handleStorageChange);


  const handleConnect = useCallback((platform, forceLogin = false) => {
    if (!accountId) return;
    const apiBase = process.env.REACT_APP_ACCOUNT_API || 'http://localhost:7000';
    let url = `${apiBase}/platform-connections/${platform.toLowerCase()}/connect`;

    if (platform.toLowerCase() === 'xero' && forceLogin) {
      url += '?prompt=login%20consent';
    }

    const width = 750;
    const height = 800;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    // Dynamic popup name for all platforms (Xero, QuickBooks, MYOB)
    const popupName = `Connect ${platform.charAt(0).toUpperCase() + platform.slice(1)}`;

    window.open(url, popupName, `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
  }, [accountId]);

  return { handleConnect };
}